import { useCallback, useEffect, useState } from "react";
import { api, apiErrorDetail, type ArtifactPublicView } from "../lib/api";

const MAX_SHARED_WITH = 20;
const EMAIL_RE =
  /^[a-z0-9][a-z0-9._%+'-]*@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

interface ShareModalProps {
  /** Slug of the artifact whose sharing lists are being managed. */
  slug: string;
  /**
   * The caller's relationship to the artifact. Only ``"owner"`` may change the
   * lists — the PUT 403s for anyone else, so an editor gets a read-only view.
   */
  access: ArtifactPublicView["access"];
  open: boolean;
  onClose: () => void;
  /** Called after a successful save so the card can refresh its count. */
  onChange?: () => void;
}

/** The two sharing lists, kept together: an editor is always also a viewer. */
export interface SharingLists {
  shared_with: string[];
  editors: string[];
}

/**
 * Drop one person from both lists. Removing a viewer who is also an editor
 * must demote them in the same breath — the server 422s a stranded editor.
 */
export function removePerson(lists: SharingLists, email: string): SharingLists {
  return {
    shared_with: lists.shared_with.filter((e) => e !== email),
    editors: lists.editors.filter((e) => e !== email),
  };
}

/** Flip one person's "can edit" bit. Someone not on the viewer list is a no-op. */
export function toggleEditor(lists: SharingLists, email: string): SharingLists {
  if (!lists.shared_with.includes(email)) return lists;
  return {
    ...lists,
    editors: lists.editors.includes(email)
      ? lists.editors.filter((e) => e !== email)
      : [...lists.editors, email],
  };
}

/**
 * Normalise a raw input into a comparable lowercase address, or return null
 * when it is not shaped like an email at all. Shape only: which *domains* may
 * be shared with is decided server-side against the admin allowlist (spec
 * §A10), so a syntactically valid foreign address passes here and is rejected
 * (422) by the backend if its domain is not allowlisted.
 */
export function normaliseShareEmail(raw: string): string | null {
  const email = raw.trim().toLowerCase();
  return EMAIL_RE.test(email) ? email : null;
}

/**
 * Modal for per-person sharing: "Ana can see this, Bruno can also edit it,
 * nobody else". Replace-whole-list semantics — Save PUTs both lists entire.
 *
 * Owner-only for writes; an editor may read the lists (the GET admits them)
 * and sees them read-only, because the PUT would 403.
 */
export function ShareModal({
  slug,
  access,
  open,
  onClose,
  onChange,
}: ShareModalProps) {
  const [lists, setLists] = useState<SharingLists>({
    shared_with: [],
    editors: [],
  });
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Only a successful GET authorises editing: Save PUTs the whole list, so
  // editing a list we never loaded would silently revoke every unseen grant.
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setDirty(false);
    setDraft("");
    setLoaded(false);
    setLists({ shared_with: [], editors: [] });
    api
      .getSharing(slug)
      .then((view) => {
        setLists({ shared_with: view.shared_with, editors: view.editors });
        setLoaded(true);
      })
      .catch((e) => {
        // Session expiry navigates away; don't flash the sentinel as an error.
        if ((e as Error).message !== "redirecting-to-login") setError(apiErrorDetail(e));
      })
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  if (!open) return null;

  // Editors may read the lists but not change them — the PUT is owner-only.
  const editable = loaded && !loading && access === "owner";
  const list = lists.shared_with;

  /** Validate the typed address and move it into the pending list. */
  function addDraft() {
    if (!editable) return;
    const email = normaliseShareEmail(draft);
    if (!email) {
      setError(
        "Enter a valid email address. The domain must be allowlisted by an admin (datacamp.com is the default).",
      );
      return;
    }
    if (list.includes(email)) {
      setDraft("");
      return;
    }
    if (list.length >= MAX_SHARED_WITH) {
      setError(
        `At most ${MAX_SHARED_WITH} people — use visibility for wider audiences.`,
      );
      return;
    }
    // New people are viewers; "can edit" is an explicit second step.
    setLists({ ...lists, shared_with: [...list, email] });
    setDraft("");
    setDirty(true);
    setError(null);
  }

  /** Drop one person from both lists (saved on Save, not immediately). */
  function remove(email: string) {
    if (!editable) return; // same gate as addDraft/save — never edit an unloaded list
    setLists(removePerson(lists, email));
    setDirty(true);
  }

  /** Promote/demote one person to editor (saved on Save, not immediately). */
  function toggle(email: string) {
    if (!editable) return;
    setLists(toggleEditor(lists, email));
    setDirty(true);
  }

  /** PUT both pending lists, flushing any typed-but-unadded address first. */
  async function save() {
    if (!editable) return; // never PUT a list we did not load
    let toSave = list;
    if (draft.trim()) {
      // A typed-but-unadded address on Save means "share with them too" —
      // silently dropping it (or silently saving without it) loses the grant
      // the user thinks they just made.
      const pending = normaliseShareEmail(draft);
      if (!pending) {
        setError(
          "Enter a valid email address. The domain must be allowlisted by an admin (datacamp.com is the default).",
        );
        return;
      }
      if (!list.includes(pending)) toSave = [...list, pending];
      setLists({ ...lists, shared_with: toSave });
      setDraft("");
      // Keep Save re-clickable if the PUT below fails — without this the
      // flushed grant would sit in the list behind a dead Save button.
      setDirty(true);
    }
    setBusy(true);
    setError(null);
    try {
      // Both lists loaded, so both are replaced whole. Editors are filtered to
      // the viewer list defensively — the server 422s a stranded editor.
      const view = await api.setSharing(
        slug,
        toSave,
        lists.editors.filter((e) => toSave.includes(e)),
      );
      setLists({ shared_with: view.shared_with, editors: view.editors });
      setDirty(false);
      onChange?.();
      onClose();
    } catch (e) {
      // 422 detail from the canonical validator (it names the allowed domains).
      setError(apiErrorDetail(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="Share artifact"
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">Share with people</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md text-slate-400 hover:text-slate-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <p className="mb-3 text-sm text-slate-500">
          Named colleagues can view this artifact even while it is private —
          and like any viewer they can use its interactive features, including
          shared storage.
          {access === "owner" && (
            <>
              {" "}
              Tick <span className="font-medium">can edit</span> to also let
              someone change its content; visibility and this sharing list
              stay owner-only.
            </>
          )}
        </p>

        {access !== "owner" && (
          <p className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Editors can see who an artifact is shared with, but only its owner
            can change these lists.
          </p>
        )}

        {access === "owner" && (
          <>
            <div className="mb-1 flex gap-2">
              <input
                type="text"
                value={draft}
                disabled={!editable}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addDraft();
                  }
                }}
                placeholder="colleague@datacamp.com"
                className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm disabled:bg-slate-50 disabled:text-slate-400"
              />
              <button
                type="button"
                onClick={addDraft}
                disabled={!editable}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
              >
                Add
              </button>
            </div>

            <p className="mb-3 text-xs text-slate-400">
              People outside datacamp.com need their domain allowlisted by an
              admin, and can&apos;t sign in until guest access ships — the
              grant stays dormant.
            </p>
          </>
        )}

        {loading ? (
          <p className="mb-3 text-sm text-slate-500">Loading…</p>
        ) : !loaded ? (
          <p className="mb-3 text-sm text-slate-500">
            Sharing list unavailable — nothing can be changed until it loads.{" "}
            <button
              type="button"
              onClick={load}
              className="rounded-md border border-slate-300 px-2 py-0.5 text-xs hover:bg-slate-50"
            >
              Retry
            </button>
          </p>
        ) : list.length === 0 ? (
          <p className="mb-3 text-sm text-slate-400">
            Not shared with anyone yet.
          </p>
        ) : (
          <ul className="mb-3 max-h-56 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
            {list.map((email) => (
              <li
                key={email}
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
              >
                <span className="truncate">{email}</span>
                <div className="flex shrink-0 items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-slate-500">
                    <input
                      type="checkbox"
                      checked={lists.editors.includes(email)}
                      disabled={!editable}
                      onChange={() => toggle(email)}
                      className="h-3.5 w-3.5 rounded border-slate-300"
                    />
                    can edit
                  </label>
                  {editable && (
                    <button
                      type="button"
                      onClick={() => remove(email)}
                      className="rounded-md px-2 py-0.5 text-xs text-slate-400 hover:bg-rose-50 hover:text-rose-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {editable && (
          <p className="mb-3 text-xs text-slate-400">
            {list.length} / {MAX_SHARED_WITH} people
          </p>
        )}

        {error && (
          <p className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-1.5 text-sm hover:bg-slate-50"
          >
            {access === "owner" ? "Cancel" : "Close"}
          </button>
          {access === "owner" && (
            <button
              type="button"
              onClick={save}
              disabled={busy || !editable || (!dirty && !draft.trim())}
              className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
